import store from '@/store/store'
import router from '@/router'
import UserRepository from '@/js/api/UserRepository'
import OrganizationRepository from '@/js/api/OrganizationRepository'
import urlUtil from '@/js/utils/urlUtil'
import { ACCOUNT_TYPE_ORGANIZATION, ACCOUNT_TYPE_PARTICIPANT } from './dashboard'
import { ParticipantSegment, ParticipantPath, MY_PAGES_ORGANIZATION_ROOT_PATH } from '@/router/myPagesPaths'

/** Localised mypages URL for an account (used by account switcher). */
function accountUrlFor(account) {
  const path = account.type === ACCOUNT_TYPE_ORGANIZATION
    ? MY_PAGES_ORGANIZATION_ROOT_PATH + '/' + (account.organizationIdentifier || account.identifier || '') : ParticipantPath.ROOT
  return urlUtil.getLocalisedUrl(path)
}

export const MANAGEMENT_ROLES = ['TRAINER', 'OWNER', 'ADMIN']
export const PARTICIPANT_ROLE = 'PARTICIPANT'
/**
 * Roles allowed to change how the organization works. Mirrors the @PreAuthorize on
 * SOrganizationController, which already restricts organization settings to OWNER and ADMIN -
 * this makes the UI agree with it instead of showing a door that returns an error.
 *
 * Deliberately not TRAINER: settings are configuration, changed a few times a year by whoever
 * owns the studio, not daily work for staff.
 */
export const CONFIGURATION_ROLES = ['OWNER', 'ADMIN']

/*
 * TODO (with team invites, [F9]): settle the role model instead of keeping three lists here that
 * mirror @PreAuthorize rules by hand. Nothing keeps the two sides in step, so a backend rule can
 * change and this file will keep hiding — or showing — the wrong door. Inviting someone into a
 * role means deciding which roles exist and what each may do, so that work is the place to fix it.
 * See docs/features/settings/team-invites-plan.md -> "Open question — the role model".
 */

const getDefaultState = () => {
  return {
    token: null,
    user: null,
    accounts: [], // raw from API: [{ organization_id, organization_name, organization_identifier, roles }]
    defaultOrganizationId: null
  }
}

/** Orgs where user has at least one management role (TRAINER/OWNER/ADMIN). */
function managementAccountsFrom(accounts) {
  if (!accounts || !accounts.length) return []
  //First remove Participant role from accounts roles array
  let tmpAccounts = accounts.map(acc => ({
    ...acc,
    roles: acc.roles && acc.roles.filter(r => r !== PARTICIPANT_ROLE)
  }))
  //filter only accounts that has at least 1 role
  tmpAccounts = tmpAccounts.filter(acc => acc.roles && acc.roles.length > 0)
  return tmpAccounts
}

/** Orgs where user has PARTICIPANT role (for participant view / "Mani pieraksti"). Includes orgs where user also has OWNER/TRAINER/ADMIN so they can switch between management and participant for the same org. */
function personalAccountOrganizationsFrom(accounts) {
  if (!accounts || !accounts.length) return []
  return accounts.filter(acc => acc.roles && acc.roles.includes(PARTICIPANT_ROLE))
}


/** Set dashboard active account by organization id (null = personal if available). */
async function setDashboardAccountByDefault(store, defaultOrganizationId) {
  const accounts = store.getters['user/accountsForSwitcher']
  const account = accounts.find(acc => acc.organizationId === defaultOrganizationId) ?? null
  if (account) await store.dispatch('dashboard/setAccount', account)
}

/** Single place to apply accounts to state (DRY: used by setUser and setAccounts). */
function applyAccounts(state, accounts) {
  state.accounts = accounts && Array.isArray(accounts) ? accounts : []
}

export default {
  namespaced: true,
  state: getDefaultState(),

  mutations: {
    resetState (state) {
      Object.assign(state, getDefaultState())
    },
    setUser (state, payload) {
      state.token = payload.token
      state.defaultOrganizationId = payload.default_organization_id ?? null
      applyAccounts(state, payload.accounts)
      state.user = payload.user
    },
    setUserDetails (state, user) {
      state.user = {
        ...state.user,
        ...user
      }
    },
    logout (state) {
      state.token = null
      state.user = null
      state.defaultOrganizationId = null
      state.accounts = []
    },
    /** Replace accounts with latest from API (used by reloadAccount). */
    setAccounts (state, accounts) {
      applyAccounts(state, accounts)
    }
  },

  actions: {
    resetState ({ commit }) {
      commit('resetState')
    },

    updateUserDetails ({ commit }, user) {
      commit('setUserDetails', user)
    },

    async authorize ({ commit }, userPayload) {
      commit('setUser', userPayload)
      await setDashboardAccountByDefault(store, userPayload.default_organization_id ?? null)
    },

    /**
     * Reload accounts from API and update state (e.g. after creating a new organization).
     * Reuses getAccounts and the same setAccounts mutation used by authorize.
     */
    async reloadAccounts ({ commit }) {
      const accounts = await UserRepository.getAccounts()
      commit('setAccounts', accounts)
    },

    refreshUserData ({ commit }) {
      UserRepository.getMe()
      .then((user) => {
        commit('setUserDetails', user)
      })
      .catch((error) => {
        throw error
      })
      
    },

    logout () {
      store.dispatch('user/removeLoggedInUserData')
      store.dispatch('dashboard/resetState')
      store.dispatch('membership/resetState')
      store.dispatch('membershipTemplate/resetState')
      store.dispatch('context/resetState')
    },

    removeLoggedInUserData ({ commit }) {
      commit('logout')
      router.go()
    }
  },
  getters: {
    token: state => state.token,
    user: state => state.user,
    userId: state => state.user ? state.user.id : null,
    isLoggedIn: state => !!state.token,
    name: state => state.user && state.user.name ? state.user.name : null,
    isEmailVerified: state => state.user ? !!state.user.emailVerified : false,
    defaultOrganizationId: state => state.defaultOrganizationId,
    /** Orgs where user has TRAINER/OWNER/ADMIN. */
    managementAccounts: state => managementAccountsFrom(state.accounts),
    /**
     * Whether the signed-in user may configure the given organization (OWNER or ADMIN).
     * Used to hide the settings area from trainers - the API enforces the same rule.
     */
    canConfigureOrganization: state => organizationId => {
      if (!organizationId) return false
      const account = (state.accounts || []).find(acc => acc.organization_id === organizationId)
      if (!account || !account.roles) return false
      return account.roles.some(role => CONFIGURATION_ROLES.includes(role))
    },
    /** Orgs where user is only PARTICIPANT (for personal account view). */
    personalAccountOrganizations: state => personalAccountOrganizationsFrom(state.accounts),
    /**
     * Flat list for account switcher: each management org as one item + one personal item if user has participant orgs.
     * Each item: { type: ACCOUNT_TYPE_ORGANIZATION | ACCOUNT_TYPE_PARTICIPANT, orgId, displayName, roles, organizationIdentifier, logo_url }.
     */
    accountsForSwitcher: (state, getters) => {
      const management = getters.managementAccounts
      const participantOrgs = getters.personalAccountOrganizations
      //Here we convert to processOrganization to format logo_url - otherwise we don't need to run this process here
      const list = management.map(acc => OrganizationRepository._processOrganization({
        type: ACCOUNT_TYPE_ORGANIZATION,
        organizationId: acc.organization_id,
        displayName: acc.organization_name,
        roles: acc.roles || [],
        organizationIdentifier: acc.organization_identifier,
        logo_url: acc.organization_logo_url || null
      }))
      if (participantOrgs.length > 0) {
        list.push({
          type: ACCOUNT_TYPE_PARTICIPANT,
          organizationId: null,
          displayName: state.user && state.user.name ? state.user.name : null,
          roles: [],
          organizationIdentifier: ParticipantSegment.ROOT,
          organizations: participantOrgs.map(o =>
            OrganizationRepository._processOrganization({
              id: o.organization_id,
              name: o.organization_name,
              identifier: o.organization_identifier,
              logo_url: o.organization_logo_url || null
            })
          )
        })
      }
      return list.map(account => ({ ...account, account_url: accountUrlFor(account) }))
    },
    mixpanelUser: state => {
      if (state.user) {
        return state.user.id
      }
      let userEmail = localStorage.getItem('join-email')
      if (userEmail){
        return userEmail
      }
      return null
    }
  }
}